import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import LandingPage from './landing/LandingPage';

// Lazy so the heavy editor/pdf/collab/cloud tree is split into its own chunk
// and only downloaded once the visitor enters the app — the landing page then
// paints from a tiny initial bundle, which matters most on mobile data.
const App = React.lazy(() => import('./App'));

// Set the first time a visitor enters the app, so returning visitors on the
// same device skip the splash and go straight to their work (the editor is
// "the main event when loaded").
const ENTERED_KEY = 'yaqat.entered';

function readEntered(): boolean {
  try {
    return localStorage.getItem(ENTERED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Which view to show on load. A same-tab reload preserves the current history
 * entry's state, so if the visitor had navigated back to the landing page,
 * `history.state` still says 'landing' and we keep them there on refresh. Only
 * a fresh navigation (no view state yet) falls back to the "entered" flag so
 * returning visitors still skip the splash.
 */
function readInitialView(): { mounted: boolean; showLanding: boolean } {
  let view: string | undefined;
  try {
    view = (window.history.state as { yaqatView?: string } | null)?.yaqatView;
  } catch {
    /* history unavailable */
  }
  if (view === 'app') return { mounted: true, showLanding: false };
  if (view === 'landing') return { mounted: false, showLanding: true };
  const entered = readEntered();
  return { mounted: entered, showLanding: !entered };
}

function AppLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
      <div className="text-gray-600 dark:text-gray-400">Loading…</div>
    </div>
  );
}

/**
 * Landing/app gate.
 *
 * Two independent pieces of state instead of one boolean, so returning "home"
 * doesn't tear the app down:
 *  - `mounted` — has the app ever been entered this session? Once true it stays
 *    true, so <App/> (and its editor + live P2P collab) keeps running even while
 *    the landing page is shown over it.
 *  - `showLanding` — is the landing page currently visible (as an overlay once
 *    the app is mounted)?
 *
 * The browser Back button is wired without a routing library: the landing page
 * is the history baseline and the app is a pushed state on top of it, so Back
 * from the app returns to the landing page (and the logo does the same). No URL
 * path change, so no server-side SPA-fallback config is needed.
 */
export default function Root() {
  const initial = useRef(readInitialView());
  const [mounted, setMounted] = useState(initial.current.mounted);
  const [showLanding, setShowLanding] = useState(initial.current.showLanding);
  const didInitHistory = useRef(false);

  useEffect(() => {
    // Establish the history baseline exactly once (StrictMode re-runs effects).
    if (!didInitHistory.current) {
      didInitHistory.current = true;
      try {
        const existing = (window.history.state as { yaqatView?: string } | null)?.yaqatView;
        // A same-tab reload keeps the current entry (and the whole back stack)
        // with its view state, so don't touch it — that's what lets a refresh
        // on the landing page stay on the landing page. Only a fresh navigation
        // (no view state) needs a baseline: landing, with the app pushed on top
        // when a returning visitor boots straight into it (so Back reaches it).
        if (!existing) {
          window.history.replaceState({ yaqatView: 'landing' }, '');
          if (initial.current.mounted) {
            window.history.pushState({ yaqatView: 'app' }, '');
          }
        }
      } catch {
        /* history unavailable — the logo still returns home, just not Back */
      }
    }

    const onPop = (e: PopStateEvent) => {
      const view = (e.state as { yaqatView?: string } | null)?.yaqatView;
      if (view === 'app') {
        setMounted(true);
        setShowLanding(false);
      } else {
        setShowLanding(true);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const enter = useCallback(() => {
    try {
      localStorage.setItem(ENTERED_KEY, '1');
    } catch {
      /* private mode / storage disabled — enter anyway for this session */
    }
    setMounted(true);
    setShowLanding(false);
    try {
      if ((window.history.state as { yaqatView?: string } | null)?.yaqatView !== 'app') {
        window.history.pushState({ yaqatView: 'app' }, '');
      }
    } catch {
      /* no history — view state above is enough */
    }
  }, []);

  const goHome = useCallback(() => {
    // Prefer stepping Back (keeps the history stack clean and symmetric); fall
    // back to just showing the overlay if there's no app entry to pop.
    try {
      if ((window.history.state as { yaqatView?: string } | null)?.yaqatView === 'app') {
        window.history.back(); // fires popstate -> showLanding = true
        return;
      }
    } catch {
      /* fall through */
    }
    setShowLanding(true);
  }, []);

  return (
    <>
      {mounted && (
        <Suspense fallback={<AppLoading />}>
          <App onGoHome={goHome} />
        </Suspense>
      )}
      {showLanding && <LandingPage onEnter={enter} overlay={mounted} />}
    </>
  );
}
